"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { CloseButton } from "@/components/ui/close-button";
import { popOwnEntry, pushBackEntry, touchEnabled } from "@/lib/backStack";

/**
 * نافذة اختيار من تصميم الموقع لكلّ <select> على شاشات اللمس (قرار المالك 2026-10-08، الخيار 1).
 *
 * لماذا: نافذة الاختيار على أندرويد يرسمها النظام — تعكس الترتيب للعربية (الدائرة يمينًا) لكنّها
 * تترك النصّ يسارًا، ولا تقرأ `dir` على <select> ولا `direction` على <option> (جُرِّب — صور المالك).
 * والموقع فيه 252 قائمة منسدلة في 65 ملفًّا.
 *
 * كيف: مُركَّب مرّة واحدة في التخطيط الجذريّ. على الأجهزة التي مؤشّرها لمس (`pointer: coarse`) يعترض
 * فتح أيّ <select> (mousedown/click في طور الالتقاط) ويعرض نافذة RTL بألوان الثيم. عند الاختيار يضبط
 * `select.value` ويُطلق `input` و`change` — وReact يقرأ onChange للـselect من حدث `change` مباشرة،
 * فيعمل كلّ كود الموقع كما هو بلا تعديل أيّ صفحة. لا أثر على الكمبيوتر (مؤشّر دقيق).
 * المستثنى: القائمة المعطّلة، و`multiple`، و`data-native-select` (لمن يحتاج نافذة النظام عمدًا).
 * للتجربة على الكمبيوتر: localStorage["mc-touch-select"]="force".
 *
 * الإغلاق (بلاغ المالك: قائمة طويلة تملأ الشاشة فلا مكان للضغط خارجها): زرّ × في رأس ثابت، والنافذة
 * حتّى 70% من الشاشة (الغلاف `flex-col` يُخرجها من قاعدة النوافذ العامّة التي كانت تمدّها إلى 100dvh)،
 * وزرّ الرجوع في الهاتف يغلقها (سجلّ تاريخ واحد يُضاف عند الفتح). وخانة بحث حين تزيد الخيارات على 10.
 */

type Opt = { value: string; label: string; disabled: boolean };
type Open = { select: HTMLSelectElement; title: string; options: Opt[]; value: string; dir: "rtl" | "ltr" };


function titleOf(sel: HTMLSelectElement): string {
  const aria = sel.getAttribute("aria-label");
  if (aria) return aria;
  // the label's own words — a select inside its label would add its options («علىجهاز وساموسام…», 2026-10-31)
  const label = sel.labels?.[0];
  if (!label) return "";
  const own = label.cloneNode(true) as HTMLElement;
  own.querySelectorAll("select").forEach((s) => s.remove());
  return own.textContent?.replace(/\s+/g, " ").trim() || "";
}

function apply(sel: HTMLSelectElement, v: string) {
  if (sel.value === v) return;
  sel.value = v;
  sel.dispatchEvent(new Event("input", { bubbles: true }));
  sel.dispatchEvent(new Event("change", { bubbles: true }));
}

export function TouchSelectPicker({ force }: { force?: boolean } = {}) {
  const [open, setOpen] = useState<Open | null>(null);
  const [q, setQ] = useState("");
  const pushed = useRef(false); // أضفنا سجلّ تاريخ عند الفتح ⇒ الإغلاق بغير الرجوع يسحبه
  // اختيار ينتظر انتهاء سحب سجلّ التاريخ: يُطبَّق بعد popstate لا قبله — وإلّا فصفحة يغيّر onChange فيها
  // المسار (router.push) يُعيدها history.back() إلى الخلف
  const pending = useRef<{ sel: HTMLSelectElement; value: string } | null>(null);

  const close = useCallback(() => {
    setOpen(null);
    setQ("");
    if (pushed.current) {
      pushed.current = false;
      popOwnEntry();
    }
  }, []);

  useEffect(() => {
    const intercept = (e: Event) => {
      const sel = (e.target as Element | null)?.closest?.("select") as HTMLSelectElement | null;
      if (!sel || sel.disabled || sel.multiple || sel.hasAttribute("data-native-select")) return;
      if (!touchEnabled(force)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type !== "mousedown") return; // click يُمنع فقط (فتح واحد)
      sel.blur();
      setOpen({
        select: sel,
        title: titleOf(sel),
        value: sel.value,
        dir: getComputedStyle(sel).direction === "ltr" ? "ltr" : "rtl",
        options: Array.from(sel.options)
          .filter((o) => !o.hidden)
          .map((o) => ({ value: o.value, label: o.text, disabled: o.disabled })),
      });
    };
    document.addEventListener("mousedown", intercept, true);
    document.addEventListener("click", intercept, true);
    return () => {
      document.removeEventListener("mousedown", intercept, true);
      document.removeEventListener("click", intercept, true);
    };
  }, [force]);

  const isOpen = !!open;
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    // زرّ الرجوع: سجلّ واحد عند الفتح؛ الرجوع يستهلكه ويغلق النافذة بلا مغادرة الصفحة
    pushBackEntry("select"); // يحمل حالة Next — وإلّا أعاد الموجّه تحميل الصفحة إن رجع المتصفّح إلى هذا السجلّ
    pushed.current = true;
    const onPop = () => {
      pushed.current = false;
      setOpen(null);
      setQ("");
      const p = pending.current;
      pending.current = null;
      if (p) apply(p.sel, p.value);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
    };
  }, [isOpen, close]);

  if (!open) return null;

  const searchable = open.options.length > 10;
  const needle = q.trim().toLowerCase();
  const shown = needle ? open.options.filter((o) => o.label.toLowerCase().includes(needle)) : open.options;

  const choose = (v: string) => {
    if (pushed.current) {
      const p = { sel: open.select, value: v };
      pending.current = p;
      close(); // history.back() ⇒ popstate يطبّق الاختيار
      // احتياط: إن لم يصل popstate (لا سجلّ قبله، أو بيئة لا تطلقه) يُطبَّق الاختيار بعد 300ms — مرّة واحدة فقط
      window.setTimeout(() => {
        if (pending.current === p) {
          pending.current = null;
          apply(p.sel, p.value);
        }
      }, 300);
    } else {
      const sel = open.select;
      close();
      apply(sel, v);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-th-overlay/50 p-4"
      data-back-ignore=""
      onClick={close}
      data-testid="touch-select-overlay"
    >
      <div
        dir={open.dir}
        className="flex w-full max-w-md max-h-[70dvh] flex-col overflow-hidden rounded-2xl bg-th-surface text-th-text shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-th-border px-4 py-2 space-y-2">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-th-text-muted">{open.title || "اختر"}</span>
            <CloseButton onClick={close} />
          </div>
          {searchable && (
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="بحث…"
              aria-label="بحث في الخيارات"
              className="block w-full rounded-lg border border-th-border-strong bg-th-surface px-3 py-2 text-sm text-th-text outline-none focus:border-th-primary focus:ring-2 focus:ring-th-primary/30"
            />
          )}
        </div>
        <div role="listbox" aria-label={open.title || "اختر"} dir={open.dir} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {shown.length === 0 && <div className="px-5 py-6 text-center text-sm text-th-text-muted">لا نتائج</div>}
          {shown.map((o, i) => {
            const selected = o.value === open.value;
            return (
              <button
                key={`${o.value}-${i}`}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={o.disabled}
                onClick={() => choose(o.value)}
                className={`flex w-full items-center gap-4 px-5 py-4 text-start text-base border-b border-th-border last:border-0 disabled:opacity-40 ${
                  selected ? "bg-th-primary-light/20 font-semibold" : "hover:bg-th-surface-alt"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                    selected ? "border-th-primary" : "border-th-border-strong"
                  }`}
                >
                  {selected && <span className="h-2.5 w-2.5 rounded-full bg-th-primary" />}
                </span>
                <span className="min-w-0 flex-1">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
